import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useProducts } from "../../context/ProductContext";
import { getDisplayPrice, getOriginalPrice, isDiscountExcluded, DISCOUNT_LABEL } from "../../utils/pricing";

export function ProductCard({ product }) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { deleteProduct } = useProducts();
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const excluded = isDiscountExcluded(product);
  const discountedPrice = getDisplayPrice(product);
  const originalPrice = getOriginalPrice(product);

  // Auto "NEW" badge: shown for 7 days after creation
  const isNew = (() => {
    if (!product.created_at) return false;
    const created = new Date(product.created_at);
    if (isNaN(created.getTime())) return false;
    const diffDays = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  })();

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!showConfirm) {
      setShowConfirm(true);
      return;
    }
    setDeleting(true);
    const success = await deleteProduct(product.id);
    if (!success) {
      alert("Failed to delete product");
    }
    setDeleting(false);
    setShowConfirm(false);
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setShowConfirm(false);
  };
  
  return (
    <div 
      className="group bg-zinc-900/50 rounded-lg overflow-hidden border border-white/5 hover:border-amber-500/30 transition-all cursor-pointer relative" 
      onClick={() => navigate(`/product/${product.id}`)}
      data-testid={`product-card-${product.id}`}
    >
      {!excluded && (
        <div className="absolute top-3 left-3 z-10 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
          {DISCOUNT_LABEL}
        </div>
      )}
      {isNew && (
        <div className="absolute top-3 left-3 z-10 mt-8 bg-gradient-to-r from-amber-400 to-yellow-300 text-black text-[10px] font-extrabold px-2 py-1 rounded tracking-widest shadow-lg shadow-amber-500/30 animate-pulse" data-testid={`new-badge-${product.id}`}>
          ✦ NEW
        </div>
      )}
      
      {/* Admin Delete Button */}
      {isAdmin && (
        <div className="absolute top-3 right-3 z-10">
          {showConfirm ? (
            <div className="flex gap-1" onClick={e => e.stopPropagation()}>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-500 text-white text-xs font-bold px-2 py-1 rounded transition-colors disabled:opacity-50"
                data-testid={`confirm-delete-${product.id}`}
              >
                {deleting ? "..." : "Yes"}
              </button>
              <button
                onClick={handleCancelDelete}
                className="bg-zinc-600 hover:bg-zinc-500 text-white text-xs font-bold px-2 py-1 rounded transition-colors"
                data-testid={`cancel-delete-${product.id}`}
              >
                No
              </button>
            </div>
          ) : (
            <button
              onClick={handleDelete}
              className="bg-red-600/80 hover:bg-red-500 text-white p-1.5 rounded transition-colors"
              title="Delete Product"
              data-testid={`delete-btn-${product.id}`}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      )}
      
      <div className="aspect-square overflow-hidden bg-zinc-800">
        <img src={product.image} alt={product.name} loading="lazy" width="400" height="400" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-4">
        <span className="text-amber-400/80 text-xs tracking-wider uppercase">{product.category}</span>
        <h3 className="text-white font-medium mt-1 text-lg">{product.name}</h3>
        <div className="mt-2 flex items-center gap-2">
          <p className="text-amber-400 font-bold text-xl">${discountedPrice}</p>
          {originalPrice && <p className="text-white/40 text-sm line-through">${originalPrice}</p>}
        </div>
      </div>
    </div>
  );
}
